import * as React from 'react';
import { FunctionComponent, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    SvgIcon,
    SxProps,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import LinkIcon from '@mui/icons-material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ReactComponent as ExecutivesIcon } from '../../static/sustainability_image/executives_icon.svg';
import { ReactComponent as BoardMembersIcon } from '../../static/sustainability_image/boardMembers_icon.svg';
import { ReactComponent as EthicalIcon } from '../../static/sustainability_image/ethical_icon.svg';
import { ReactComponent as PreviousArrow } from '../../static/sustainability_image/previous_arrow.svg';
import { useResponsive } from '../../contexts/ResponsiveContext';
import { useCommonContent } from '../../graphql/pimcore/modules/siteContent/hooks/useCommonContent';

import {
    AboutSustainabilityGovernanceContent,
    AboutSustainabilityGovernanceExecutivesEntry,
} from '../../graphql/pimcore/modules/siteContent/types/AboutSustainabilityContent';
import { sanitizeRichText } from '../../graphql/pimcore/modules/siteContent/tools/sanitizeRichText';
import { resolveLinkHref } from './GovernancePage';
import { truncateHtml } from './EnvironmentalTab';
import { useTabUrl } from '../common/useTabUrl';

interface GovernanceTabProps {
    cmsContent: AboutSustainabilityGovernanceContent;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
    style?: React.CSSProperties;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, style, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            style={{ cursor: 'default', ...style }}
            {...other}
        >
            {value === index && (
                <Box sx={{ marginTop: '0.42rem', width: '100%', cursor: 'default' }}>
                    <Typography component="div">{children}</Typography>
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`,
    };
}

interface CustomIconProps {
    component: React.ElementType;
    expanded: boolean;
}

const CustomIcon: FunctionComponent<CustomIconProps> = ({ component: Component, expanded }) => (
    <SvgIcon
        component={Component}
        inheritViewBox
        sx={{
            marginRight: '0.5rem',
            width: '24px',
            height: '24px',
            path: { fill: expanded ? 'white' : '#E56731' },
        }}
    />
);

const GovernanceTab: FunctionComponent<GovernanceTabProps> = ({ cmsContent }) => {
    const [expandedPanels, setExpandedPanels] = useState<string[]>([]);
    const [mobileExpanded, setMobileExpanded] = useState<number | false>(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const { contentTree: cmsCommonContent } = useCommonContent();
    const basePath = '/About/Sustainability/Governance';
    const tabKeys = ['YageoGroupExecutives', 'YageoGroupBoard', 'YageoFocus'];
    const readMoreLabel = cmsCommonContent.misc.readMoreButtonLabel.content;
    const readLessLabel = cmsCommonContent.misc.readLessButtonLabel.content;
    const maxTextLength = 300;

    const fullHtml = cmsContent.Focus.mainText.content || '';
    const { truncated, isTruncated } = truncateHtml(fullHtml, maxTextLength);
    const { value, handleChange } = useTabUrl(basePath, tabKeys);

    const handleMobileAccordionChange =
        (panel: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
            setMobileExpanded(isExpanded ? panel : false);
        };

    const { isScreenXxl, isScreenXl, isScreenLg, isScreenMd, isScreenSm, isScreenXs } =
        useResponsive();
    const isDesktop = isScreenLg || isScreenXl || isScreenXxl;

    const buttonProps: SxProps = {
        padding: isScreenSm || isScreenXs ? '1px 8px' : '4px 11px',
        gap: '10px',
        backgroundColor: '#E5673E',
        borderRadius: '5px',
        fontFamily: 'Poppins',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: isScreenSm || isScreenXs ? '16px' : '24px',
        lineHeight: '36px',
        whiteSpace: 'nowrap',
        textTransform: 'none',
        transition: 'none',
        color: 'white',
        '&:hover': {
            background: 'linear-gradient(0deg, #B75332 0%, #E5673E 100%)',
        },
    };
    const buildPanelsFromExecutiveSection = (
        section: AboutSustainabilityGovernanceExecutivesEntry,
        sectionPrefix: string
    ) => {
        if (!Array.isArray(section.entries)) return [];

        return section.entries.map((entry, idx) => ({
            id: `${sectionPrefix}-${idx}`,
            header: entry.title,
            content: Array.isArray(entry.personProfiles)
                ? entry.personProfiles.map((person) => ({
                      title: entry.title, // 如果有需要可以換成 person.name
                      items: [
                          {
                              name: person.name,
                              sanitizedContent: sanitizeRichText(person.content, ['ul', 'li'], []),
                          },
                      ],
                  }))
                : [],
        }));
    };

    const generateAccordion = (title: string, panels: Array<any>) => {
        return (
            <Accordion
                expanded={true}
                square={true}
                sx={{
                    backgroundColor: '#FAE1D8',
                    width: isScreenLg
                        ? '565px'
                        : isScreenXl
                          ? '760px'
                          : !isDesktop
                            ? '100%'
                            : '769px',
                }}
            >
                <AccordionSummary sx={{ cursor: 'default' }}>
                    <Typography
                        sx={{
                            fontSize: isScreenSm || isScreenXs ? '16px' : '30px',
                            color: '#B75332',
                            fontWeight: 700,
                            fontFamily: 'Poppins',
                            cursor: 'default',
                        }}
                    >
                        {title}
                    </Typography>
                </AccordionSummary>

                {panels.map((panel, index) => (
                    <AccordionDetails key={index} sx={{ cursor: 'default' }}>
                        <Accordion
                            expanded={panel.expanded}
                            onChange={panel.onChange}
                            disableGutters
                            sx={{
                                borderTopLeftRadius: 0,
                                borderTopRightRadius: 0,
                                cursor: 'default',
                            }}
                        >
                            <AccordionSummary
                                expandIcon={panel.expanded ? <RemoveIcon /> : <AddIcon />}
                                aria-controls={`${panel.id}-content`}
                                id={`${panel.id}-header`}
                                sx={{
                                    backgroundColor: panel.expanded ? '#F5C3B2' : 'white',
                                    cursor: 'default',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: isScreenSm || isScreenXs ? '16px' : '20px',
                                        color: '#3E3E3E',
                                        fontWeight: 500,
                                        fontFamily: 'Poppins',
                                        cursor: 'default',
                                    }}
                                >
                                    {panel.header}
                                </Typography>
                            </AccordionSummary>
                        </Accordion>
                        {panel.expanded &&
                            panel.content.map((contentItem: any, contentIndex: number) => (
                                <Accordion
                                    key={contentIndex}
                                    expanded={true}
                                    sx={{ cursor: 'default' }}
                                >
                                    <AccordionSummary
                                        aria-controls={`${panel.id}-content-${contentIndex}`}
                                        id={`${panel.id}-contain-${contentIndex}`}
                                        sx={{ cursor: 'default' }}
                                    >
                                        {contentItem.items.map((item: any, idx: number) => (
                                            <Box
                                                key={idx}
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection:
                                                        isScreenSm || isScreenXs ? 'column' : 'row',
                                                    alignItems: 'center',
                                                    width: '100%',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontFamily: 'Poppins',
                                                        fontSize: isScreenLg ? '14px' : '16px',
                                                        fontWeight:
                                                            isScreenSm || isScreenXs ? 600 : 500,
                                                        marginRight: '0.1rem',
                                                        width: isDesktop
                                                            ? '30%'
                                                            : isScreenSm || isScreenXs
                                                              ? '100%'
                                                              : '40%',
                                                        textAlign: 'center',
                                                        // isScreenSm || isScreenXs
                                                        //     ? 'left'
                                                        //     : 'center', // 小屏幕时水平居中

                                                        cursor: 'default',
                                                    }}
                                                >
                                                    {item.name}
                                                </Typography>
                                                <Box
                                                    style={{
                                                        fontFamily: 'Poppins',
                                                        fontSize: isScreenLg ? '14px' : '16px',
                                                        marginLeft: isDesktop ? '0' : '0',
                                                        maxWidth:
                                                            isScreenSm || isScreenXs
                                                                ? '75%'
                                                                : '100%',
                                                        marginTop:
                                                            isScreenSm || isScreenXs
                                                                ? '0.5rem'
                                                                : '0',
                                                        fontWeight: 400,
                                                        color: '#E5673E',
                                                        listStyleType: 'none',
                                                        cursor: 'default',
                                                        textAlign:
                                                            isScreenSm || isScreenXs
                                                                ? 'center'
                                                                : 'left',
                                                    }}
                                                >
                                                    <Box
                                                        key={idx}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            width: '100%',
                                                        }}
                                                    >
                                                        {/* 右側清單 */}
                                                        <Box
                                                            sx={{
                                                                width: isDesktop ? '100%' : '100%',
                                                            }}
                                                        >
                                                            <ul
                                                                style={{
                                                                    listStyle: 'none',
                                                                    padding: 0,
                                                                    margin: 0,
                                                                }}
                                                            >
                                                                {(() => {
                                                                    const tempDiv =
                                                                        document.createElement(
                                                                            'div'
                                                                        );
                                                                    tempDiv.innerHTML =
                                                                        item.sanitizedContent;
                                                                    const lis = Array.from(
                                                                        tempDiv.querySelectorAll(
                                                                            'li'
                                                                        )
                                                                    ).map((li) => li.innerHTML);

                                                                    return lis.map(
                                                                        (liText, liIdx) => (
                                                                            <li
                                                                                key={liIdx}
                                                                                style={{
                                                                                    display: 'flex',
                                                                                    alignItems:
                                                                                        'flex-start',
                                                                                    marginBottom:
                                                                                        '8px',
                                                                                    color: '#E5673E',
                                                                                    fontFamily:
                                                                                        'Poppins',
                                                                                    fontSize:
                                                                                        isScreenLg
                                                                                            ? '14px'
                                                                                            : '16px',
                                                                                }}
                                                                            >
                                                                                <PreviousArrow
                                                                                    style={{
                                                                                        width: '14px',
                                                                                        height: '14px',
                                                                                        marginRight:
                                                                                            '8px',
                                                                                        flexShrink: 0,
                                                                                        marginTop:
                                                                                            '4px',
                                                                                    }}
                                                                                />
                                                                                <Box
                                                                                    sx={{
                                                                                        fontSize:
                                                                                            isScreenXs
                                                                                                ? '10px'
                                                                                                : isScreenSm
                                                                                                  ? '12px'
                                                                                                  : '16px',
                                                                                    }}
                                                                                >
                                                                                    <div
                                                                                        dangerouslySetInnerHTML={{
                                                                                            __html: liText,
                                                                                        }}
                                                                                    />
                                                                                </Box>
                                                                            </li>
                                                                        )
                                                                    );
                                                                })()}
                                                            </ul>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        ))}
                                    </AccordionSummary>
                                </Accordion>
                            ))}
                    </AccordionDetails>
                ))}
            </Accordion>
        );
    };

    const handleAccordionChange =
        (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
            setExpandedPanels((prev) =>
                isExpanded ? [...prev, panel] : prev.filter((p) => p !== panel)
            );
        };
    const rawExecutivePanels = buildPanelsFromExecutiveSection(
        cmsContent.Executives.ExecutiveOfficers,
        'executive'
    );
    const rawSalesPanels = buildPanelsFromExecutiveSection(
        cmsContent.Executives.SalesBusinessGroup,
        'sales'
    );
    const rawProductPanels = buildPanelsFromExecutiveSection(
        cmsContent.Executives.ProductBusinessGroups,
        'product'
    );
    const rawCorporateInfrastructurePanels = buildPanelsFromExecutiveSection(
        cmsContent.Executives.CorporateInfrastructureUnits,
        'infra'
    );
    const rawBoardOfDirectorsPanels = buildPanelsFromExecutiveSection(
        cmsContent.BoardMembers.BoardOfDirectors,
        'boardMembers'
    );
    const rawRemunerationCommitteePanels = buildPanelsFromExecutiveSection(
        cmsContent.BoardMembers.RemunerationCommittee,
        'remunerationCommittee'
    );
    const rawAuditCommitteePanels = buildPanelsFromExecutiveSection(
        cmsContent.BoardMembers.AuditCommittee,
        'auditCommittee'
    );

    const executivePanels = rawExecutivePanels.map((panel) => ({
        ...panel,
        expanded: expandedPanels.includes(panel.id),
        onChange: handleAccordionChange(panel.id),
    }));

    const salesBusinessGroupPanels = rawSalesPanels.map((panel) => ({
        ...panel,
        expanded: expandedPanels.includes(panel.id),
        onChange: handleAccordionChange(panel.id),
    }));

    const productBusinessGroupsPanels = rawProductPanels.map((panel) => ({
        ...panel,
        expanded: expandedPanels.includes(panel.id),
        onChange: handleAccordionChange(panel.id),
    }));

    const corporateInfrastructureUnitsPanels = rawCorporateInfrastructurePanels.map((panel) => ({
        ...panel,
        expanded: expandedPanels.includes(panel.id),
        onChange: handleAccordionChange(panel.id),
    }));

    const boardOfDirectorsPanels = rawBoardOfDirectorsPanels.map((panel) => ({
        ...panel,
        expanded: expandedPanels.includes(panel.id),
        onChange: handleAccordionChange(panel.id),
    }));

    const remunerationCommitteePanels = rawRemunerationCommitteePanels.map((panel) => ({
        ...panel,
        expanded: expandedPanels.includes(panel.id),
        onChange: handleAccordionChange(panel.id),
    }));

    const auditCommitteePanels = rawAuditCommitteePanels.map((panel) => ({
        ...panel,
        expanded: expandedPanels.includes(panel.id),
        onChange: handleAccordionChange(panel.id),
    }));

    const mobileTabs = [
        {
            label: cmsContent.Executives.title.content,
            icon: ExecutivesIcon,
            content: (
                <>
                    {generateAccordion(
                        cmsContent.Executives.ExecutiveOfficers.title.content,
                        executivePanels
                    )}
                    {generateAccordion(
                        cmsContent.Executives.SalesBusinessGroup.title.content,
                        salesBusinessGroupPanels
                    )}
                    {generateAccordion(
                        cmsContent.Executives.ProductBusinessGroups.title.content,
                        productBusinessGroupsPanels
                    )}
                    {generateAccordion(
                        cmsContent.Executives.CorporateInfrastructureUnits.title.content,
                        corporateInfrastructureUnitsPanels
                    )}
                </>
            ),
        },
        {
            label: cmsContent.BoardMembers.title.content,
            icon: BoardMembersIcon,
            content: (
                <>
                    {generateAccordion(
                        cmsContent.BoardMembers.BoardOfDirectors.title.content,
                        boardOfDirectorsPanels
                    )}
                    {generateAccordion(
                        cmsContent.BoardMembers.RemunerationCommittee.title.content,
                        remunerationCommitteePanels
                    )}
                    {generateAccordion(
                        cmsContent.BoardMembers.AuditCommittee.title.content,
                        auditCommitteePanels
                    )}
                    <Accordion
                        square={true}
                        expanded={true}
                        sx={{
                            backgroundColor: '#FAE1D8',
                            width: '100%',
                        }}
                    >
                        <AccordionSummary sx={{ cursor: 'default' }}>
                            <Typography
                                sx={{
                                    fontSize: isScreenSm || isScreenXs ? '16px' : '30px',
                                    color: '#B75332',
                                    fontWeight: 700,
                                    fontFamily: 'Poppins',
                                    cursor: 'default',
                                }}
                            >
                                {cmsContent.BoardMembers.OperatingStatus.title.content}
                            </Typography>
                        </AccordionSummary>

                        {cmsContent.BoardMembers.OperatingStatus.links.links.map((link, index) => (
                            <AccordionDetails key={index} sx={{ cursor: 'default' }}>
                                <Accordion
                                    disableGutters
                                    sx={{
                                        width: '100%',
                                        borderTopLeftRadius: 0,
                                        borderTopRightRadius: 0,
                                    }}
                                >
                                    <AccordionSummary
                                        component="a"
                                        href={resolveLinkHref(link)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            width: '100%',
                                            textDecoration: 'none',
                                            backgroundColor: 'white',
                                            cursor: 'default',
                                            transition: 'background-color 0.3s',
                                            '&:hover': { backgroundColor: '#F5C3B2' },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                width: '100%',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize:
                                                        isScreenSm || isScreenXs ? '12px' : '20px',
                                                    color: '#3E3E3E',
                                                    fontWeight: 500,
                                                    fontFamily: 'Poppins',
                                                    cursor: 'inherit',
                                                }}
                                            >
                                                {link.linkText}
                                            </Typography>

                                            <LinkIcon
                                                sx={{
                                                    fontSize: isScreenSm || isScreenXs ? 20 : 35,
                                                    color: 'black',
                                                }}
                                            />
                                        </Box>
                                    </AccordionSummary>
                                </Accordion>
                            </AccordionDetails>
                        ))}
                    </Accordion>
                </>
            ),
        },
        {
            label: cmsContent.Focus.title.content,
            icon: EthicalIcon,
            content: (
                <Accordion
                    square={true}
                    sx={{
                        backgroundColor: '#FAE1D8',
                        width:
                            (isExpanded && isScreenXs) || (isExpanded && isScreenSm)
                                ? '100%'
                                : '100%',
                    }}
                >
                    <AccordionDetails sx={{ cursor: 'default' }}>
                        <Accordion
                            disableGutters
                            sx={{
                                borderTopLeftRadius: 0,
                                borderTopRightRadius: 0,
                                cursor: 'default',
                                mb: isScreenSm || isScreenXs ? '0.7rem' : '1rem',
                            }}
                        >
                            <AccordionSummary
                                aria-controls={`panel8-1-content`}
                                id={`panel8-1-header`}
                                sx={{ backgroundColor: 'white', cursor: 'default' }}
                            >
                                {(fullHtml || truncated) && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                fontSize: isScreenXs
                                                    ? '12px'
                                                    : isScreenSm
                                                      ? '13px'
                                                      : '15px',
                                                color: '#3E3E3E',
                                                fontWeight: 500,
                                                fontFamily: 'Poppins',
                                                marginBottom: '1rem',
                                                cursor: 'default',
                                            }}
                                        >
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: isExpanded
                                                        ? fullHtml || ''
                                                        : truncated || '',
                                                }}
                                            />
                                        </Box>

                                        {isTruncated && (
                                            <Button
                                                variant="contained"
                                                sx={buttonProps}
                                                onClick={() => setIsExpanded((prev) => !prev)}
                                            >
                                                {isExpanded ? readLessLabel : readMoreLabel}
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </AccordionSummary>
                        </Accordion>
                        {cmsContent.Focus.links.links.map((link, index) => (
                            <Accordion
                                disableGutters
                                key={index}
                                sx={{
                                    width: '100%',
                                    mb: '1rem',
                                    borderTopLeftRadius: 0,
                                    borderTopRightRadius: 0,
                                }}
                            >
                                <AccordionSummary
                                    component="a"
                                    href={resolveLinkHref(link)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%',
                                        textDecoration: 'none',
                                        backgroundColor: 'white',
                                        cursor: 'default',
                                        transition: 'background-color 0.3s',
                                        '&:hover': { backgroundColor: '#F5C3B2' },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            width: '100%',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize:
                                                    isScreenSm || isScreenXs ? '12px' : '20px',
                                                color: '#3E3E3E',
                                                fontWeight: 500,
                                                fontFamily: 'Poppins',
                                                cursor: 'inherit',
                                            }}
                                        >
                                            {link.linkText}
                                        </Typography>

                                        <LinkIcon
                                            sx={{
                                                fontSize: isScreenSm || isScreenXs ? 20 : 35,
                                                color: 'black',
                                            }}
                                        />
                                    </Box>
                                </AccordionSummary>
                            </Accordion>
                        ))}
                    </AccordionDetails>
                </Accordion>
            ),
        },
    ];

    return (
        <>
            {isDesktop ? (
                <Box
                    sx={{
                        flexGrow: 1,
                        bgcolor: '#F8F8F8',
                        display: 'flex',
                        height: '100%',
                    }}
                >
                    <Tabs
                        orientation={isScreenMd ? 'horizontal' : 'vertical'}
                        value={value}
                        onChange={handleChange}
                        aria-label="Vertical tabs example"
                        TabIndicatorProps={{ style: { display: 'none' } }}
                        sx={{
                            width: isScreenLg ? '20rem' : '22rem',
                            position: 'relative',
                            cursor: 'default',
                            '& .MuiTab-root': {
                                color: '#E5673E',
                                alignItems: 'flex-start',
                                justifyContent: 'flex-start',
                                padding: '12px',
                                margin: '5px 0',
                                position: 'relative',
                                zIndex: 1,
                                '&:hover': {
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 1.1,
                                        left: 1.5,
                                        width: '19rem',
                                        height: '60px',
                                        backgroundColor: '#FCF0EC',
                                        zIndex: -1,
                                    },
                                },
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 1.1,
                                    left: 1.5,
                                    width: isScreenLg ? '18rem' : '19rem',
                                    height: '60px',
                                    backgroundColor: 'white',
                                    boxShadow:
                                        '1px 4px 8px rgba(0, 0, 0, 0.2),-1px 4px 8px rgba(0, 0, 0, 0.2)',
                                    zIndex: -1,
                                },
                            },
                            '& .Mui-selected': {
                                color: 'white!important',
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                                zIndex: 2,
                                path: { fill: 'white' },
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 1.1,
                                    left: 1.5,
                                    width: isScreenLg ? '20rem' : '22rem',
                                    height: '60px',
                                    backgroundImage:
                                        'linear-gradient(90deg, #B75332 30%, #FAE1D8 100%)',
                                    zIndex: -1,
                                },
                            },
                            '& .Mui-selected::before': {
                                boxShadow: 'none',
                            },
                        }}
                    >
                        <Tab
                            icon={
                                <ExecutivesIcon
                                    fill={'none'}
                                    style={{ marginTop: '0.5rem', cursor: 'default' }}
                                />
                            }
                            iconPosition="start"
                            label={
                                <Typography
                                    sx={{
                                        marginTop: '0.5rem',
                                        marginLeft: '0.2rem',
                                        fontFamily: 'Poppins',
                                        lineHeight: '22px',
                                        fontSize: '20px',
                                        fontWeight: 500,
                                        textTransform: 'none',
                                    }}
                                >
                                    {cmsContent.Executives.title.content}
                                </Typography>
                            }
                            {...a11yProps(0)}
                        />
                        <Tab
                            icon={
                                <BoardMembersIcon fill={'none'} style={{ marginTop: '0.5rem' }} />
                            }
                            iconPosition="start"
                            label={
                                <Typography
                                    sx={{
                                        marginTop: '0.5rem',
                                        marginLeft: '0.2rem',
                                        fontFamily: 'Poppins',
                                        lineHeight: '22px',
                                        fontSize: '20px',
                                        fontWeight: 500,
                                        textTransform: 'none',
                                    }}
                                >
                                    {cmsContent.BoardMembers.title.content}
                                </Typography>
                            }
                            {...a11yProps(1)}
                        />
                        <Tab
                            icon={<EthicalIcon fill={'none'} style={{ marginTop: '0.5rem' }} />}
                            iconPosition="start"
                            label={
                                <Typography
                                    sx={{
                                        marginTop: '0.5rem',
                                        marginLeft: '0.2rem',
                                        fontFamily: 'Poppins',
                                        lineHeight: '22px',
                                        fontSize: '20px',
                                        fontWeight: 500,
                                        textTransform: 'none',
                                    }}
                                >
                                    {cmsContent.Focus.title.content}
                                </Typography>
                            }
                            {...a11yProps(2)}
                        />
                    </Tabs>

                    <TabPanel value={value} index={0}>
                        <>
                            {generateAccordion(
                                cmsContent.Executives.ExecutiveOfficers.title.content,
                                executivePanels
                            )}
                            {generateAccordion(
                                cmsContent.Executives.SalesBusinessGroup.title.content,
                                salesBusinessGroupPanels
                            )}
                            {generateAccordion(
                                cmsContent.Executives.ProductBusinessGroups.title.content,
                                productBusinessGroupsPanels
                            )}
                            {generateAccordion(
                                cmsContent.Executives.CorporateInfrastructureUnits.title.content,
                                corporateInfrastructureUnitsPanels
                            )}
                        </>
                    </TabPanel>

                    <TabPanel value={value} index={1}>
                        <>
                            {generateAccordion(
                                cmsContent.BoardMembers.BoardOfDirectors.title.content,
                                boardOfDirectorsPanels
                            )}
                            {generateAccordion(
                                cmsContent.BoardMembers.RemunerationCommittee.title.content,
                                remunerationCommitteePanels
                            )}
                            {generateAccordion(
                                cmsContent.BoardMembers.AuditCommittee.title.content,
                                auditCommitteePanels
                            )}
                            <Accordion
                                expanded={true}
                                square={true}
                                sx={{
                                    backgroundColor: '#FAE1D8',
                                    width: isScreenLg
                                        ? '565px'
                                        : isScreenXl
                                          ? '760px'
                                          : !isDesktop
                                            ? '100%'
                                            : '769px',
                                }}
                            >
                                <AccordionSummary>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column', // 垂直排列
                                            width: '100%', // 讓內部撐滿寬度
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '30px',
                                                color: '#B75332',
                                                fontWeight: 700,
                                                fontFamily: 'Poppins',
                                            }}
                                        >
                                            {cmsContent.BoardMembers.OperatingStatus.title.content}
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                {cmsContent.BoardMembers.OperatingStatus.links.links.map(
                                    (link, index) => (
                                        <AccordionDetails key={index} sx={{ cursor: 'default' }}>
                                            <Accordion
                                                disableGutters
                                                sx={{
                                                    borderTopLeftRadius: 0,
                                                    borderTopRightRadius: 0,
                                                }}
                                            >
                                                <AccordionSummary
                                                    component="a"
                                                    href={resolveLinkHref(link)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        width: '100%',
                                                        textDecoration: 'none',
                                                        backgroundColor: 'white',
                                                        cursor: 'default',
                                                        transition: 'background-color 0.3s',
                                                        '&:hover': { backgroundColor: '#F5C3B2' },
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            width: '100%',
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize:
                                                                    isScreenSm || isScreenXs
                                                                        ? '16px'
                                                                        : '20px',
                                                                color: '#3E3E3E',
                                                                fontWeight: 500,
                                                                fontFamily: 'Poppins',
                                                                cursor: 'inherit',
                                                            }}
                                                        >
                                                            {link.linkText}
                                                        </Typography>

                                                        <LinkIcon
                                                            sx={{ fontSize: 35, color: 'black' }}
                                                        />
                                                    </Box>
                                                </AccordionSummary>
                                            </Accordion>
                                        </AccordionDetails>
                                    )
                                )}
                            </Accordion>
                        </>
                    </TabPanel>
                    <TabPanel value={value} index={2}>
                        <Accordion
                            expanded={true}
                            square={true}
                            sx={{
                                backgroundColor: '#FAE1D8',
                                width: isScreenLg ? '565px' : isScreenXl ? '760px' : '833px',
                            }}
                        >
                            <AccordionSummary>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column', // 垂直排列
                                        width: '100%', // 讓內部撐滿寬度
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: '30px',
                                            color: '#B75332',
                                            fontWeight: 700,
                                            fontFamily: 'Poppins',
                                        }}
                                    >
                                        {cmsContent.Focus.title.content}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '20px',
                                                color: '#3E3E3E',
                                                fontWeight: 500,
                                                fontFamily: 'Poppins',
                                                marginBottom: '1rem',
                                                cursor: 'default',
                                            }}
                                            component="div"
                                        >
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: isExpanded ? fullHtml : truncated,
                                                }}
                                            />
                                        </Typography>

                                        {isTruncated && (
                                            <Button
                                                variant="contained"
                                                sx={buttonProps}
                                                onClick={() => setIsExpanded((prev) => !prev)}
                                            >
                                                {isExpanded ? readLessLabel : readMoreLabel}
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            </AccordionSummary>
                            {cmsContent.Focus.links.links.map((link, index) => (
                                <AccordionDetails key={index} sx={{ cursor: 'default' }}>
                                    <Accordion
                                        disableGutters
                                        sx={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
                                    >
                                        <AccordionSummary
                                            component="a"
                                            href={resolveLinkHref(link)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                width: '100%',
                                                textDecoration: 'none',
                                                backgroundColor: 'white',
                                                cursor: 'default',
                                                transition: 'background-color 0.3s',
                                                '&:hover': { backgroundColor: '#F5C3B2' },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    width: '100%',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize:
                                                            isScreenSm || isScreenXs
                                                                ? '16px'
                                                                : '20px',
                                                        color: '#3E3E3E',
                                                        fontWeight: 500,
                                                        fontFamily: 'Poppins',
                                                        cursor: 'inherit',
                                                    }}
                                                >
                                                    {link.linkText}
                                                </Typography>

                                                <LinkIcon sx={{ fontSize: 35, color: 'black' }} />
                                            </Box>
                                        </AccordionSummary>
                                    </Accordion>
                                </AccordionDetails>
                            ))}
                        </Accordion>
                    </TabPanel>
                </Box>
            ) : (
                <Box
                    sx={{
                        flexGrow: 1,
                        bgcolor: '#F8F8F8',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        alignItems: 'center',
                    }}
                >
                    {mobileTabs.map((tab, index) => (
                        <Accordion
                            key={index}
                            expanded={mobileExpanded === index}
                            onChange={handleMobileAccordionChange(index)}
                            sx={{
                                marginBottom: '1rem',
                                backgroundColor: '#F2F2F2',
                                width: isScreenLg
                                    ? '565px'
                                    : isScreenMd
                                      ? '650px'
                                      : isScreenSm
                                        ? '422px'
                                        : isScreenXs
                                          ? '295px'
                                          : '833px',
                                borderLeft: '2px solid #E0E0E0',
                                borderRight: '2px solid #E0E0E0',
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    backgroundColor: mobileExpanded === index ? '#B75332' : 'white',
                                    color: mobileExpanded === index ? 'white' : '#E56731',
                                    '& .MuiSvgIcon-root': {
                                        color: mobileExpanded === index ? 'white' : '#E56731',
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <CustomIcon
                                        component={tab.icon}
                                        expanded={mobileExpanded === index}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: isScreenXs ? '16px' : '20px',
                                            fontWeight: 500,
                                            marginLeft: '0.5rem',
                                        }}
                                    >
                                        {tab.label}
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>{tab.content}</AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            )}
        </>
    );
};

export default GovernanceTab;
